import "@testing-library/jest-dom";
import React from "react";
import {
  act,
  fireEvent,
  render,
  screen,
  waitFor,
} from "@testing-library/react";
import { CanvasKanbanRenderer } from "./CanvasKanbanRenderer";
import { useViewDataQuery } from "../../../hooks/useViewDataQuery";
import { useUpdateRowMutation } from "../../../hooks/useViewDataQuery";
import { openCanvasNavigation } from "../canvasNavigation";

jest.mock("../../../../../providers/WorkspaceProvider", () => ({
  useWorkspaceContext: () => ({ workspace: { id: "ws-1" } }),
}));
jest.mock("../../../hooks/useViewDataQuery", () => ({
  useViewDataQuery: jest.fn(),
  useUpdateRowMutation: jest.fn(),
}));
jest.mock("../canvasNavigation", () => ({
  openCanvasNavigation: jest.fn(),
}));
jest.mock("react-router-dom", () => ({
  useNavigate: () => jest.fn(),
  useLocation: () => ({ pathname: "/board", search: "" }),
}));
jest.mock("../../../../../utils/useAuthFetch", () => ({
  useAuthFetch: () => jest.fn(),
}));
jest.mock("../CanvasRuntimeContext", () => ({
  useCanvasRuntime: () => ({
    canvasId: "canvas-1",
    workspaceId: "ws-1",
    rowId: "row-9",
    interactionBus: {
      getRuntimeFilters: () => [],
    },
  }),
}));

// The drag library cannot run a pointer drag in jsdom: capture its handler instead.
const dragState: {
  onDragEnd?: (result: unknown) => void;
  disabled: Record<string, boolean>;
  droppableDisabled: Record<string, boolean>;
} = { disabled: {}, droppableDisabled: {} };
jest.mock("@hello-pangea/dnd", () => ({
  DragDropContext: ({
    children,
    onDragEnd,
  }: {
    children: React.ReactNode;
    onDragEnd: (result: unknown) => void;
  }) => {
    dragState.onDragEnd = onDragEnd;
    return <div>{children}</div>;
  },
  Droppable: ({
    droppableId,
    isDropDisabled,
    children,
  }: {
    droppableId: string;
    isDropDisabled?: boolean;
    children: (...args: unknown[]) => React.ReactNode;
  }) => {
    dragState.droppableDisabled[droppableId] = isDropDisabled === true;
    return (
      <div>
        {children(
          { innerRef: jest.fn(), droppableProps: {}, placeholder: null },
          {},
        )}
      </div>
    );
  },
  Draggable: ({
    draggableId,
    isDragDisabled,
    children,
  }: {
    draggableId: string;
    isDragDisabled?: boolean;
    children: (...args: unknown[]) => React.ReactNode;
  }) => {
    dragState.disabled[draggableId] = isDragDisabled === true;
    return (
      <>
        {children(
          { innerRef: jest.fn(), draggableProps: {}, dragHandleProps: {} },
          {},
        )}
      </>
    );
  },
}));

const query = useViewDataQuery as jest.Mock;
const updateRow = useUpdateRowMutation as jest.Mock;
const navigateAction = {
  type: "NAVIGATE",
  config: { target_canvas_id: "detail-canvas", record_source: "selected" },
};

const element = (overrides: Record<string, unknown> = {}, access?: string) => ({
  id: "kanban-1",
  type: "kanban",
  gridColumn: 1,
  gridRow: 1,
  columnSpan: 12,
  rowSpan: 5,
  effectiveAccess: access,
  props: {
    title: "Candidates",
    data_source_alias: "people",
    kanban_config: {
      status_field: "stage",
      columns: ["Applied", "Interview", "Offer"],
      title_field: "name",
      subtitle_field: "role",
      ...overrides,
    },
  },
});
const dataSources = [
  {
    id: "people",
    name: "people",
    type: "view" as const,
    view_id: 7,
    config: {},
  },
];
const people = [
  { _rowId: "r1", name: "Ada", role: "Engineer", stage: "Applied" },
  { _rowId: "r2", name: "Grace", role: "Admiral", stage: "Interview" },
  { _rowId: "r3", name: "Linus", role: "Kernel", stage: "Applied" },
  { _rowId: "r4", name: "Alan", role: "Theory", stage: "Archived" },
];

const renderBoard = (props = element()) =>
  render(
    <CanvasKanbanRenderer
      element={props as never}
      dataSources={dataSources}
      canvasId="canvas-1"
    />,
  );

beforeEach(() => {
  dragState.disabled = {};
  dragState.droppableDisabled = {};
  (openCanvasNavigation as jest.Mock).mockReset();
  (openCanvasNavigation as jest.Mock).mockResolvedValue(undefined);
  query.mockReset();
  query.mockReturnValue({
    data: { rows: people, totalCount: people.length },
    isLoading: false,
    error: null,
  });
  updateRow.mockReturnValue({ mutateAsync: jest.fn().mockResolvedValue({}) });
});

describe("CanvasKanbanRenderer", () => {
  it("draws the configured lanes in order with their cards and an Other lane for unmatched rows", () => {
    renderBoard();
    const lanes = screen
      .getAllByTestId(/^lane-/)
      .map((lane) => lane.dataset.testid);
    expect(lanes).toEqual([
      "lane-Applied",
      "lane-Interview",
      "lane-Offer",
      "lane-__other__",
    ]);
    const applied = screen.getByTestId("lane-Applied");
    expect(applied).toHaveTextContent("Ada");
    expect(applied).toHaveTextContent("Engineer");
    expect(applied).toHaveTextContent("Linus");
    expect(screen.getByTestId("lane-Interview")).toHaveTextContent("Grace");
    expect(screen.getByTestId("lane-Offer")).toHaveTextContent("No cards");
    expect(screen.getByTestId("lane-__other__")).toHaveTextContent("Alan");
    expect(screen.getByText("Applied (2)")).toBeInTheDocument();
  });

  it("pages in every row so a board is never cut at the first 100 cards", () => {
    const make = (from: number, count: number) =>
      Array.from({ length: count }, (_, index) => ({
        _rowId: `p${from + index}`,
        name: `Person ${from + index}`,
        stage: "Applied",
      }));
    query.mockImplementation((_viewId: string, params: { page?: number }) => ({
      data:
        (params.page ?? 1) === 1
          ? { rows: make(1, 100), totalCount: 130 }
          : { rows: make(101, 30), totalCount: 130 },
      isLoading: false,
      error: null,
    }));
    renderBoard();
    expect(query).toHaveBeenCalledWith(
      "7",
      expect.objectContaining({ page: 2, pageSize: 100 }),
    );
    expect(screen.getByText("Applied (130)")).toBeInTheDocument();
  });

  it("writes the new status through the row update when a card is dropped in another lane", async () => {
    const mutateAsync = jest.fn().mockResolvedValue({});
    updateRow.mockReturnValue({ mutateAsync });
    renderBoard();
    dragState.onDragEnd?.({
      draggableId: "r1",
      source: { droppableId: "Applied", index: 0 },
      destination: { droppableId: "Offer", index: 0 },
    });
    await waitFor(() =>
      expect(mutateAsync).toHaveBeenCalledWith({
        viewId: "7",
        rowId: "r1",
        data: { stage: "Offer" },
        context: expect.objectContaining({
          canvasId: "canvas-1",
          elementId: "kanban-1",
        }),
      }),
    );
    // the card moves at once, before the server answers
    expect(screen.getByTestId("lane-Offer")).toHaveTextContent("Ada");
  });

  it("ignores a drop in the same lane, outside any lane or on the Other lane", () => {
    const mutateAsync = jest.fn().mockResolvedValue({});
    updateRow.mockReturnValue({ mutateAsync });
    renderBoard();
    dragState.onDragEnd?.({
      draggableId: "r1",
      source: { droppableId: "Applied", index: 0 },
      destination: { droppableId: "Applied", index: 1 },
    });
    dragState.onDragEnd?.({
      draggableId: "r1",
      source: { droppableId: "Applied", index: 0 },
      destination: null,
    });
    dragState.onDragEnd?.({
      draggableId: "r1",
      source: { droppableId: "Applied", index: 0 },
      destination: { droppableId: "__other__", index: 0 },
    });
    expect(mutateAsync).not.toHaveBeenCalled();
    expect(dragState.droppableDisabled.__other__).toBe(true);
  });

  it("has no drag at all on a read-only view", () => {
    const mutateAsync = jest.fn().mockResolvedValue({});
    updateRow.mockReturnValue({ mutateAsync });
    renderBoard(element({}, "read"));
    expect(dragState.disabled).toEqual({
      r1: true,
      r2: true,
      r3: true,
      r4: true,
    });
    dragState.onDragEnd?.({
      draggableId: "r1",
      source: { droppableId: "Applied", index: 0 },
      destination: { droppableId: "Offer", index: 0 },
    });
    expect(mutateAsync).not.toHaveBeenCalled();
  });

  it("puts a failed move back and says so", async () => {
    updateRow.mockReturnValue({
      mutateAsync: jest
        .fn()
        .mockRejectedValue(new Error("Row was changed by someone else")),
    });
    renderBoard();
    dragState.onDragEnd?.({
      draggableId: "r1",
      source: { droppableId: "Applied", index: 0 },
      destination: { droppableId: "Offer", index: 0 },
    });
    expect(await screen.findByRole("alert")).toHaveTextContent(
      "Could not move the card: Row was changed by someone else",
    );
    expect(screen.getByTestId("lane-Applied")).toHaveTextContent("Ada");
  });

  it("opens the configured detail canvas for the clicked card's record", () => {
    renderBoard(element({ card_click_action: navigateAction }));
    fireEvent.click(screen.getByText("Grace"));
    expect(openCanvasNavigation).toHaveBeenCalledWith(
      navigateAction.config,
      expect.objectContaining({
        workspaceId: "ws-1",
        currentRowId: "row-9",
        selectedRowId: "r2",
      }),
    );
  });

  it("does nothing on a card click when no click-through is configured", () => {
    renderBoard();
    fireEvent.click(screen.getByText("Grace"));
    expect(openCanvasNavigation).not.toHaveBeenCalled();
  });

  it("shows loading, empty and error states", () => {
    query.mockReturnValueOnce({ isLoading: true, error: null });
    const { rerender } = renderBoard();
    expect(screen.getByText("Loading board…")).toBeInTheDocument();
    query.mockReturnValueOnce({
      isLoading: false,
      error: new Error("Forbidden"),
    });
    rerender(
      <CanvasKanbanRenderer
        element={element() as never}
        dataSources={dataSources}
        canvasId="canvas-1"
      />,
    );
    expect(screen.getByText("Unable to load board.")).toBeInTheDocument();
  });

  it("asks for a view and field mapping instead of drawing an unbound board", () => {
    render(
      <CanvasKanbanRenderer
        element={
          {
            ...element(),
            props: { title: "Board", data_source_alias: "people" },
          } as never
        }
        dataSources={dataSources}
        canvasId="canvas-1"
      />,
    );
    expect(
      screen.getByText(
        "Configure a view, status field, columns and title field for this board.",
      ),
    ).toBeInTheDocument();
  });

  it("does not carry a successful move over to another data source", async () => {
    const mutateAsync = jest.fn().mockResolvedValue({});
    updateRow.mockReturnValue({ mutateAsync });
    const { rerender } = renderBoard();
    dragState.onDragEnd?.({
      draggableId: "r1",
      source: { droppableId: "Applied", index: 0 },
      destination: { droppableId: "Offer", index: 0 },
    });
    await waitFor(() => expect(mutateAsync).toHaveBeenCalledTimes(1));
    expect(screen.getByTestId("lane-Offer")).toHaveTextContent("Ada");
    // another view where the same row id is still Applied
    rerender(
      <CanvasKanbanRenderer
        element={element() as never}
        dataSources={[{ ...dataSources[0], view_id: 8 }]}
        canvasId="canvas-1"
      />,
    );
    expect(screen.getByTestId("lane-Applied")).toHaveTextContent("Ada");
    expect(screen.getByTestId("lane-Offer")).not.toHaveTextContent("Ada");
  });

  it("refuses a second move of a card whose first move is still pending and disables its drag", async () => {
    let rejectFirst: (reason: Error) => void = () => undefined;
    const mutateAsync = jest
      .fn()
      .mockImplementationOnce(
        () =>
          new Promise((_resolve, reject) => {
            rejectFirst = reject;
          }),
      )
      .mockResolvedValue({});
    updateRow.mockReturnValue({ mutateAsync });
    renderBoard();
    act(() => {
      dragState.onDragEnd?.({
        draggableId: "r1",
        source: { droppableId: "Applied", index: 0 },
        destination: { droppableId: "Interview", index: 0 },
      });
    });
    expect(dragState.disabled.r1).toBe(true);
    act(() => {
      dragState.onDragEnd?.({
        draggableId: "r1",
        source: { droppableId: "Interview", index: 0 },
        destination: { droppableId: "Offer", index: 0 },
      });
    });
    expect(mutateAsync).toHaveBeenCalledTimes(1);
    await act(async () => rejectFirst(new Error("Row was changed")));
    expect(screen.getByTestId("lane-Applied")).toHaveTextContent("Ada");
    expect(dragState.disabled.r1).toBe(false);
  });

  it("lets a callback from an old context touch neither the new board nor its error line", async () => {
    let rejectFirst: (reason: Error) => void = () => undefined;
    updateRow.mockReturnValue({
      mutateAsync: jest.fn().mockImplementationOnce(
        () =>
          new Promise((_resolve, reject) => {
            rejectFirst = reject;
          }),
      ),
    });
    const { rerender } = renderBoard();
    dragState.onDragEnd?.({
      draggableId: "r1",
      source: { droppableId: "Applied", index: 0 },
      destination: { droppableId: "Offer", index: 0 },
    });
    rerender(
      <CanvasKanbanRenderer
        element={element() as never}
        dataSources={[{ ...dataSources[0], view_id: 8 }]}
        canvasId="canvas-1"
      />,
    );
    await act(async () => rejectFirst(new Error("Row was changed")));
    expect(screen.queryByRole("alert")).toBeNull();
    expect(screen.getByTestId("lane-Applied")).toHaveTextContent("Ada");
  });

  it("lets an old rejection neither undo nor annotate a newer confirmed move after leaving and returning to the same context", async () => {
    let rejectFirst: (reason: Error) => void = () => undefined;
    const mutateAsync = jest
      .fn()
      .mockImplementationOnce(
        () =>
          new Promise((_resolve, reject) => {
            rejectFirst = reject;
          }),
      )
      .mockResolvedValue({});
    updateRow.mockReturnValue({ mutateAsync });
    const view = (id: number) => (
      <CanvasKanbanRenderer
        element={element() as never}
        dataSources={[{ ...dataSources[0], view_id: id }]}
        canvasId="canvas-1"
      />
    );
    const drop = (to: string, from = "Applied") =>
      act(() => {
        dragState.onDragEnd?.({
          draggableId: "r1",
          source: { droppableId: from, index: 0 },
          destination: { droppableId: to, index: 0 },
        });
      });
    const { rerender } = render(view(7));
    drop("Interview"); // request 1, still pending
    rerender(view(8));
    drop("Interview"); // another view: request 2, confirmed
    await waitFor(() => expect(mutateAsync).toHaveBeenCalledTimes(2));
    rerender(view(7));
    drop("Offer"); // back in the first view: request 3, confirmed
    await waitFor(() => expect(mutateAsync).toHaveBeenCalledTimes(3));
    expect(screen.getByTestId("lane-Offer")).toHaveTextContent("Ada");
    await act(async () => rejectFirst(new Error("Row was changed")));
    expect(screen.getByTestId("lane-Offer")).toHaveTextContent("Ada");
    expect(screen.queryByRole("alert")).toBeNull();
  });
});
